/* eslint-disable max-lines */
/* eslint-disable camelcase */
import { call, takeLatest, select, takeEvery } from 'redux-saga/effects';

import { trackHomeLeftBeforeLoad } from 'pages/dashboard/hooks/useAnalytics/actions';
import { trackPlansAndBilling } from 'pages/Settings/actions';
import {
  loadedPublishSuggestedCompany,
  loadedSkipSuggestedCompany,
  loadSuggestedBannerClicked,
  loadSuggestedBannerClosed,
} from '../Employers/hooks/useSuggestedCompanies/actions';
import { trackEvent } from '../../helpers/analytics';
import { activeNetworkSelector } from '../../redux/selectors';
import { triggerTrackEvent } from '../CompanyProfile/actions';
import { EXPIRATION_OPTIONS } from '../../components/molecules/RenewJob/constants';
import {
  loadedBulkForceJobVisibility,
  loadedDeleteJob,
  loadedExtendJob,
  loadedForceJobVisibility,
  loadedFeatureJob,
  loadedCloseJob,
  loadedBulkForceAllJobsVisibility,
} from '../../hooks/useJobActions/actions';
import { loadedRequestExportJobAlertSubscribers } from '../Settings/components/ExportJobAlertSubscribers/redux/actions';

function* loadedExtendJobEvent({ payload }) {
  const { job, companyId, expiresIn, source } = payload;
  const activeNetwork = yield select(activeNetworkSelector);
  const { isManager } = activeNetwork;
  const { id: jobId } = job;

  const eventProperties = {
    organization_id: companyId,
    job_id: jobId,
    is_network_admin: isManager,
  };

  if (expiresIn) {
    eventProperties.expiration_date = EXPIRATION_OPTIONS.find(({ value }) => value === parseInt(expiresIn, 10)).label;
  }

  yield call(trackEvent, `${source}:renew_job`, eventProperties);
}

function* triggerTrackEventEvent({ payload }) {
  const { name, eventProperties } = payload;
  const activeNetwork = yield select(activeNetworkSelector);
  const { isManager } = activeNetwork;
  yield call(trackEvent, name, {
    ...eventProperties,
    is_network_admin: isManager,
  });
}

function* loadedCloseJobEvent({ payload }) {
  const {
    jobId: job_id,
    companyId: organization_id,
    networkId: network_id,
    isNetworkManager: is_network_admin,
    source,
  } = payload;

  yield call(trackEvent, `${source}:close_job`, {
    job_id,
    organization_id,
    network_id,
    is_network_admin,
  });
}

function* loadedDeleteJobEvent({ payload }) {
  const { job } = payload;
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id, isManager: is_network_admin } = activeNetwork;

  yield call(trackEvent, 'jobs:delete_job', {
    job_id: job.id,
    organization_id: job.organization.id,
    network_id,
    is_network_admin,
  });
}

function* loadedRequestExportJobAlertSubscribersEvent() {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id, isManager: is_network_admin } = activeNetwork;

  yield call(trackEvent, 'job_seeker_notifications:subscribers_dump_request', {
    network_id,
    is_network_admin,
  });
}

function* loadedFeatureJobEvent({ payload }) {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id, isManager: is_network_admin } = activeNetwork;
  const { job, featured, source } = payload;

  yield call(trackEvent, `${source}:update_featured_status`, {
    network_id,
    is_network_admin,
    job_id: job.id,
    organization_id: job.organization.id,
    featured: featured === 'featured',
  });
}

function* loadedBulkForceJobVisibilityEvent({ payload }) {
  const { source, ids: selected_jobs, forcedVisibility: visibility } = payload;
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id, isManager: is_network_admin } = activeNetwork;

  yield call(trackEvent, `${source}:update_visibility`, {
    network_id,
    is_network_admin,
    multiple_selection: true,
    selected_jobs,
    visibility,
  });
}

function* loadedBulkForceAllJobVisibilityEvent({ payload }) {
  const { source, forcedVisibility: visibility, companyId: company_id } = payload;
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id, isManager: is_network_admin } = activeNetwork;

  yield call(trackEvent, `${source}:update_visibility`, {
    network_id,
    is_network_admin,
    multiple_selection: true,
    visibility,
    company_id,
  });
}

function* loadedForceJobVisibilityEvent({ payload: { job, forcedVisibility, source } }) {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id, isManager: is_network_admin } = activeNetwork;

  yield call(trackEvent, `${source}:update_visibility`, {
    network_id,
    is_network_admin,
    multiple_selection: false,
    organization_id: job.organization.id,
    job_id: job.id,
    visibility: forcedVisibility,
  });
}

function* loadSuggestedCompaniesPublishedEvent({ payload: { companyId } }) {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, `employers:suggestion_added`, {
    network_id,
    source: 'new_suggestions',
    organization_id: companyId,
  });
}

function* loadSuggestedCompaniesSkippedEvent({ payload: { companyId } }) {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, `employers:suggestion_skipped`, {
    network_id,
    organization_id: companyId,
  });
}

function* loadSuggestedCompaniesSkippedReasonEvent({ payload: { companyId, reason, otherValue } }) {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, `employers:suggestion_skipped_reason`, {
    network_id,
    organization_id: companyId,
    reason: reason === 'Other' && otherValue ? otherValue : reason,
  });
}

function* loadSuggestedCompaniesBannerClickedEvent() {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, `new_suggested_companies_banner:cta_click`, {
    network_id,
    source: 'employer',
  });
}

function* loadSuggestedCompaniesBannerClosedEvent() {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, `new_suggested_companies_banner:close`, {
    network_id,
    source: 'employer',
  });
}

function* trackHomeLeftBeforeLoadEvent() {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, 'home:left_before_loaded', {
    network_id,
  });
}

function* trackPlansAndBillingEvent({ payload: { eventName } }) {
  const activeNetwork = yield select(activeNetworkSelector);
  const { id: network_id } = activeNetwork;

  yield call(trackEvent, `plans_billing:${eventName}`, {
    network_id,
  });
}

/**
 * When we want to track something instead of filling up the saga with events we should but them here.
 * We can listen for the action and track here as we want. Because the component wraps the whole site the saga
 * is able to listen for all actions from all the children.
 */
export default function* analyticsTrackingSaga() {
  yield takeEvery(triggerTrackEvent().type, triggerTrackEventEvent);
  yield takeLatest(loadedExtendJob().type, loadedExtendJobEvent);
  yield takeLatest(loadedCloseJob().type, loadedCloseJobEvent);
  yield takeLatest(loadedDeleteJob().type, loadedDeleteJobEvent);
  yield takeLatest(loadedRequestExportJobAlertSubscribers().type, loadedRequestExportJobAlertSubscribersEvent);
  yield takeLatest(loadedFeatureJob().type, loadedFeatureJobEvent);
  yield takeLatest(loadedBulkForceJobVisibility().type, loadedBulkForceJobVisibilityEvent);
  yield takeLatest(loadedBulkForceAllJobsVisibility().type, loadedBulkForceAllJobVisibilityEvent);
  yield takeLatest(loadedForceJobVisibility().type, loadedForceJobVisibilityEvent);
  yield takeLatest(loadedSkipSuggestedCompany().type, loadSuggestedCompaniesSkippedEvent);
  yield takeLatest(loadedSkipSuggestedCompany().type, loadSuggestedCompaniesSkippedReasonEvent);
  yield takeLatest(loadSuggestedBannerClicked().type, loadSuggestedCompaniesBannerClickedEvent);
  yield takeLatest(loadSuggestedBannerClosed().type, loadSuggestedCompaniesBannerClosedEvent);
  yield takeLatest(loadedPublishSuggestedCompany().type, loadSuggestedCompaniesPublishedEvent);
  yield takeLatest(trackHomeLeftBeforeLoad().type, trackHomeLeftBeforeLoadEvent);
  yield takeLatest(trackPlansAndBilling().type, trackPlansAndBillingEvent);
}
